import "./ManualProjectEntryForm.css";

import { ArrowRoundRightLightIcon, RoundedPlusSignIcon } from "../../../assets";
import { StandardConstants } from "../../../constants";
import { ErrorBox, InformationBox } from "../../cards";
import { Button, Select, TextInput } from "../../forms";
import { Column, Row } from "../../layout";
import { getErrorMessages, hasFieldError } from "../utils";
import { ManualProjectEntryFormProps } from "./models";

export const ManualProjectEntryForm = ({
  value,
  projectTypeOptions,
  standardOptions,
  verifierOptions,
  countryOptions = [],
  onChange,
  onAddProject,
  onNext,
  nextLabel = "Next",
  isLoading = false,
  disabled = false,
  corners = "default",
  errors,
  showActions = true,
  onRemove,
  heading,
}: ManualProjectEntryFormProps): JSX.Element => {
  const isNoStandardSelected = value.standardUuid === StandardConstants.NO_STANDARD_UUID;

  return (
    <div className="ManualProjectEntryForm">
      {(heading || onRemove) && (
        <Row spacingV="ml">
          <Column span={12}>
            <div className="ManualProjectEntryForm_header">
              {heading && <h3>{heading}</h3>}
              {onRemove && (
                <Button
                  variant="secondary"
                  text="Remove"
                  corners={corners}
                  disabled={isLoading || disabled}
                  onClick={onRemove}
                />
              )}
            </div>
          </Column>
        </Row>
      )}

      {errors && errors.length > 0 && (
        <Row spacingV="ml">
          <Column span={12}>
            <ErrorBox title="Please solve the below issues before continuing" text={getErrorMessages(errors)} />
          </Column>
        </Row>
      )}

      <Row spacingV="ml">
        <Column span={6}>
          <TextInput
            disabled={disabled}
            label="Project name"
            name="project-name"
            value={value.displayName}
            onChange={(displayName) => onChange({ ...value, displayName })}
            hasError={hasFieldError(errors, "displayName")}
          />
        </Column>
        <Column span={6}>
          <Select
            disabled={disabled}
            label="Project type"
            placeholder="Select"
            corners={corners}
            data={projectTypeOptions}
            value={value.projectTypeUuid}
            onChange={(projectTypeUuid) => onChange({ ...value, projectTypeUuid })}
            hasError={hasFieldError(errors, "projectTypeUuid")}
          />
        </Column>
      </Row>

      <Row spacingV="ml">
        <Column span={6}>
          <Select
            disabled={disabled}
            label="Standard"
            placeholder="Select"
            corners={corners}
            data={standardOptions}
            value={value.standardUuid}
            onChange={(standardUuid) => onChange({ ...value, standardUuid })}
            hasError={hasFieldError(errors, "standardUuid")}
          />
        </Column>
        <Column span={6}>
          <Select
            disabled={disabled}
            label="Validation and verification body"
            placeholder="Select"
            corners={corners}
            data={verifierOptions}
            value={value.vvbUuid}
            onChange={(vvbUuid) => onChange({ ...value, vvbUuid })}
            hasError={hasFieldError(errors, "vvbUuid")}
          />
        </Column>
      </Row>

      {isNoStandardSelected && (
        <Row spacingV="ml">
          <Column span={6}>
            <InformationBox variant="warning">
              <p className="body2">
                You must assign an established governance standard in order to progress the project beyond its
                Feasibility phase.
              </p>
            </InformationBox>
          </Column>
        </Row>
      )}

      <Row spacingV="ml">
        <Column span={6}>
          <TextInput
            disabled={disabled}
            label="Region / County / State"
            name="project-region"
            optional
            value={value.addressRegion}
            onChange={(addressRegion) => onChange({ ...value, addressRegion })}
          />
        </Column>
        <Column span={6}>
          <TextInput
            disabled={disabled}
            label="Postcode"
            name="project-postcode"
            optional
            value={value.addressPostcode}
            onChange={(addressPostcode) => onChange({ ...value, addressPostcode })}
          />
        </Column>
      </Row>

      <Row spacingV="ml">
        <Column span={6}>
          <TextInput
            disabled={disabled}
            label="Latitude"
            name="project-latitude"
            optional
            value={value.latitude}
            onChange={(latitude) => onChange({ ...value, latitude })}
          />
        </Column>
        <Column span={6}>
          <TextInput
            disabled={disabled}
            label="Longitude"
            name="project-longitude"
            optional
            value={value.longitude}
            onChange={(longitude) => onChange({ ...value, longitude })}
          />
        </Column>
      </Row>

      <Row spacingV="ml">
        <Column span={6}>
          <TextInput
            disabled={disabled}
            label="Grid reference"
            name="project-grid-reference"
            optional
            value={value.gridReference}
            onChange={(gridReference) => onChange({ ...value, gridReference })}
          />
        </Column>
        <Column span={6}>
          <Select
            disabled={disabled}
            label="Country"
            placeholder="Select"
            corners={corners}
            data={countryOptions}
            value={value.addressCountryCode}
            onChange={(addressCountryCode) => onChange({ ...value, addressCountryCode })}
          />
        </Column>
      </Row>

      <Row spacingV="4xl">
        <Column span={6}>
          <TextInput
            disabled={disabled}
            label="Area (ha)"
            name="project-area"
            type="number"
            optional
            value={value.areaNetHa === null ? "" : String(value.areaNetHa)}
            onChange={(areaNetHa) => onChange({ ...value, areaNetHa: areaNetHa === "" ? null : Number(areaNetHa) })}
          />
        </Column>
      </Row>

      {showActions && (
        <Row>
          <Column span={12}>
            <div className="ManualProjectEntryForm_actions">
              <Button
                variant="secondary"
                text="Add project"
                icon={<RoundedPlusSignIcon />}
                corners={corners}
                disabled={isLoading || disabled}
                onClick={onAddProject}
              />
              <Button
                text={nextLabel}
                icon={<ArrowRoundRightLightIcon />}
                corners={corners}
                isLoading={isLoading}
                onClick={onNext}
              />
            </div>
          </Column>
        </Row>
      )}
    </div>
  );
};
